import type { MeasureComponentProps, MeasureComponentState } from "@component/MeasureComponent";
import { MeasureComponent } from "@component/MeasureComponent";
import { GoogleAnalytics4 } from "@utils/google-analytics-4";
import type { GA4EventParameters } from "@utils/google-analytics-4.type";
import { logError } from "@utils/sentry-error";
import { updateUrl } from "@utils/url-update";
import autobind from "autobind-decorator";
import { PromisePool, getUrlParameters } from "bm-ui-kit";
import type { ProgressButtonState } from "bm-ui-kit";
import lzString from "lz-string";
import type { ReactNode } from "react";
import { Breadcrumb, Container, Dropdown, DropdownButton } from "react-bootstrap";
import { InView } from "react-intersection-observer";
import { ProgressBar } from "../ProgressBar/ProgressBar";
import { StepByStepContainer } from "./Steps/StepByStepContainer";
import type { StepsConfig } from "./types";
import "./style.scss";

export interface AbstractStepByStepCalculatorProps extends MeasureComponentProps {
	shouldRenderProgressBar?: boolean;
}

export interface AbstractStepByStepCalculatorState<FormFields, Response> extends MeasureComponentState {
	activeStep: string;
	results: Response;
	submitState: ProgressButtonState;
	values: FormFields;
	wasVisible: boolean;
	filledSteps: Array<string>;
	stepNumber: number;
}

export abstract class AbstractStepByStepCalculator<
	FormFields,
	Request,
	Response,
	Props extends AbstractStepByStepCalculatorProps = AbstractStepByStepCalculatorProps,
	State extends AbstractStepByStepCalculatorState<FormFields, Response> = AbstractStepByStepCalculatorState<
		FormFields,
		Response
	>,
> extends MeasureComponent<Props, State> {
	protected readonly promisePool = new PromisePool();
	private currentStepIsInvalid = false;
	private wantToFillFieldsFromUrl = false;
	private fieldsFilledFromUrl = false;
	protected firstCalculation = true;
	protected calculationId = undefined;

	public constructor(props: Props) {
		super(props);
		this.state = this.getInitialState();
	}

	protected getInitialState(): State {
		let values = {};
		let activeStep = "step-1";
		const params = getUrlParameters();

		if (params.data) {
			values = JSON.parse(lzString.decompressFromEncodedURIComponent(params.data));
			this.wantToFillFieldsFromUrl = true;
			activeStep = "results";
		}

		return {
			activeStep,
			results: null,
			submitState: undefined,
			values,
			wasVisible: false,
			filledSteps: [],
			stepNumber: 0,
		} as unknown as State;
	}

	protected abstract initSteps(): Array<StepsConfig>;

	public componentWillUnmount(): void {
		this.promisePool.cancel();
	}

	private fillSteps(steps: Array<StepsConfig>): Array<StepsConfig> {
		const { filledSteps } = this.state;

		return steps.map((step) => ({ ...step, filled: filledSteps.includes(step.stepName) }));
	}

	public render(): ReactNode {
		const { activeStep, wasVisible, stepNumber } = this.state;

		return (
			<InView
				as="div"
				className={this.getClassName()}
				{...GoogleAnalytics4.getInViewProps()}
				skip={wasVisible}
				onChange={this.onVisibilityChange}
			>
				<StepByStepContainer
					onStep={this.onSubmitStep}
					activeStep={activeStep}
					ga4Event={this.getCommonEventParameters()}
					stepNumber={stepNumber}
				>
					{this.renderStepContainer()}
				</StepByStepContainer>
			</InView>
		);
	}

	private filledAllBeforeSteps(currentStepIndex: number, steps: Array<StepsConfig>): boolean {
		if (currentStepIndex > 0) {
			const notFilled = steps.find((step, index) => {
				// ha nincs ki töltve visszaad egy találatot
				// ha visszaadot találatot akkor az nem jó
				if (index < currentStepIndex) {
					return step?.filled === false;
				}

				return false;
			});

			return notFilled === undefined;
		}

		return false;
	}

	protected renderBreadcumb(steps: Array<StepsConfig>): ReactNode {
		const { activeStep } = this.context;

		return (
			<Breadcrumb bsPrefix="breadcrumb breadcrumb-labeled" className="d-none d-xl-block">
				{steps.map((item, key) => {
					const index = key + 1;
					const lastElement = index === steps.length;
					let disbledLink = !item?.filled && item.stepName !== activeStep;

					if (this.filledAllBeforeSteps(key, steps) === true) {
						disbledLink = false;
					}

					return (
						<Breadcrumb.Item key={`step_${key}`} linkAs="span">
							<a
								href={`#${item.stepName}`}
								onClick={(e) => {
									e.preventDefault();

									if (disbledLink === false) {
										// waiting the validator
										this.promisePool.addTimeout(400).then(() => {
											if (this.currentStepIsInvalid === false) {
												this.breadCumbClickHandler(item.stepName);
											}
										});
									}
								}}
								title=""
								className={`breadcrumb-element ${item.stepName === activeStep ? "active" : ""} ${
									disbledLink ? "disabled" : ""
								}`}
							>
								{lastElement === true ? (
									<span>{item.label}</span>
								) : (
									<>
										<span>{index}</span>
										<br />
										{item.label}
									</>
								)}
							</a>
						</Breadcrumb.Item>
					);
				})}
			</Breadcrumb>
		);
	}

	protected renderProgressBar(steps: StepsConfig[]): ReactNode {
		const { activeStep } = this.state;
		const activeStepNumber: number = steps.findIndex((item) => item.stepName === activeStep) + 1;

		return <ProgressBar stepNumber={activeStepNumber} numberOfCells={steps.length} />;
	}

	protected renderDropDown(steps: Array<StepsConfig>): ReactNode {
		const { activeStep } = this.context;
		const activeStepName = steps.find((item) => item.stepName === activeStep)?.label || "Ismeretlen";

		return (
			<DropdownButton
				id="dropdown-basic-button"
				className="d-xl-none mb-3"
				title={activeStepName}
				size="sm"
				variant="success"
			>
				{steps.map((item, key) => {
					const index = key + 1;
					const lastElement = index === steps.length;
					let disbledLink = !item?.filled && item.stepName !== activeStep;

					if (this.filledAllBeforeSteps(key, steps) === true) {
						disbledLink = false;
					}

					return (
						<Dropdown.Item
							key={`step_${key}`}
							active={item.stepName === activeStep}
							disabled={disbledLink}
							href={`#${item.stepName}`}
							onClick={(e) => {
								e.preventDefault();

								if (disbledLink === false) {
									// waiting the validator
									this.promisePool.addTimeout(400).then(() => {
										if (this.currentStepIsInvalid === false) {
											this.breadCumbClickHandler(item.stepName);
										}
									});
								}
							}}
						>
							{lastElement === true ? (
								item.label
							) : (
								<>
									{index}. {item.label}
								</>
							)}
						</Dropdown.Item>
					);
				})}
			</DropdownButton>
		);
	}

	protected renderStepContainer(): ReactNode {
		const steps = this.fillSteps(this.initSteps());
		const { shouldRenderProgressBar } = this.props;

		if (this.wantToFillFieldsFromUrl === true && this.fieldsFilledFromUrl === false) {
			this.fieldsFilledFromUrl = true;
			this.promisePool.addTimeout(10).then(() => {
				this.setState(
					{
						filledSteps: steps.map((step) => step.stepName),
					},
					() => {
						this.onStep("results");
					},
				);
			});

			return null;
		}

		return (
			<>
				<Container>
					{this.renderBreadcumb(steps)}
					{shouldRenderProgressBar ? this.renderProgressBar(steps) : this.renderDropDown(steps)}
				</Container>
				<div>
					{steps.map((item, key) => (
						<div key={`step_${key}`}>{item.content}</div>
					))}
				</div>
			</>
		);
	}

	private breadCumbClickHandler(stepName: string): void {
		this.onStep(stepName);
	}

	@autobind
	protected hasError(stepName: string): void {
		if (this.wantToFillFieldsFromUrl === true && this.fieldsFilledFromUrl === false) {
			return;
		}

		const { activeStep } = this.state;

		if (stepName === activeStep) {
			this.currentStepIsInvalid = true;
		}
	}

	@autobind
	private onSubmitStep(target: string, newValues: FormFields): void {
		const { values, filledSteps, activeStep } = this.state;
		const index = filledSteps.indexOf(activeStep);
		this.currentStepIsInvalid = false;

		if (index === -1) {
			filledSteps.push(activeStep);
		}
		this.setState(
			{
				values: { ...values, ...newValues },
				filledSteps,
			},
			() => {
				this.onStep(target, newValues);
			},
		);
	}

	protected onStepAndSendStatistics(target: string, stepValues?: FormFields): void {
		const { activeStep, stepNumber } = this.state;
		const lastStep = activeStep;

		this.setState({
			activeStep: target,
			stepNumber: stepNumber + 1,
		});

		this.sendEvent("BM_step", {
			buttonId: GoogleAnalytics4.generateId("bi"),
			stepName: lastStep,
			stepNumber: stepNumber + 1,
			...stepValues,
		});
	}

	protected onStep(target: string, stepValues?: FormFields): void {
		const { values } = this.state;

		if (target === "results") {
			updateUrl(this.generateUrl(values), "Keresés eredménye", values);
			this.promisePool.add(
				this.onStepResults()
					.then(() => {
						this.onStepAndSendStatistics(target, stepValues);
						this.setState({
							submitState: undefined,
						});
					})
					.catch((error: Error) => {
						this.setState({
							submitState: "error",
						});

						this.promisePool.addTimeout().then(() => {
							this.setState({
								submitState: undefined,
							});
						});

						logError(error);
					}),
			);
		} else {
			this.onStepAndSendStatistics(target, stepValues);
		}
	}

	protected generateUrl(search: FormFields): string {
		const { hash } = window.location;

		return `${window.location.protocol}//${window.location.host}${
			window.location.pathname
		}?data=${lzString.compressToEncodedURIComponent(JSON.stringify(search))}${hash}`;
	}

	private async onStepResults(): Promise<void> {
		this.setState({
			submitState: "loading",
		});

		return this.promisePool
			.add(this.calculate())
			.then((response) => {
				this.setState({
					results: response.results,
				});

				this.sendCalculationEvent(this.getKoMessages());
			})
			.catch((error: Error) => {
				this.setState({
					results: undefined,
				});

				logError(error);
			});
	}

	protected getKoMessages(): string[] {
		return [];
	}

	protected sendCalculationEvent(koMessages: string[] = []): void {
		const { values } = this.state;

		this.calculationId = GoogleAnalytics4.generateId("ci");

		this.sendEvent("BM_calculation", {
			hasDetailed: false,
			isDetailed: false,
			koMessages: koMessages?.join("|"),
		});

		if (this.firstCalculation) {
			this.sendEvent("BM_defaultParams", this.getDefaultValues());
			this.firstCalculation = false;
		}

		this.sendEvent("BM_calculationParams", {
			...values,
		});

		if (koMessages.toString() === "") {
			this.sendEvent("BM_result");
		}
	}

	protected setStepProps(props: any, steps: Array<StepsConfig>): any {
		return {
			...props,
			stepNumber: steps.length + 1,
			previousStep: steps[steps.length - 1]?.stepName,
		};
	}

	protected getCommonEventParameters(): Partial<GA4EventParameters> {
		return {
			calculationId: this.calculationId,
			...super.getCommonEventParameters(),
		};
	}

	protected abstract calculate(): Promise<{ request: Request; results: Response }>;

	protected getClassName(): string {
		return `step-by-step-calculator`;
	}

	protected abstract getDefaultValues(): FormFields;
}
